// src/pages/AnalysisDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import AnalysisNavigation from '../components/AnalysisNavigation';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ApiService from '../services/api';

// Import Analysis Detail Components
import {
  TeamAnalysisDetail,
  ProductAnalysisDetail,
  MarketAnalysisDetail,
  FinanceAnalysisDetail,
  StrategicFinanceAnalysisDetail,
  ElevatorPitchDetail,
  CompletenessDetail,
  InvestorSummaryDetail
} from '../components/analysis/detail';

const AnalysisDetailPage = () => {
  const { taskId, analysisType } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchAnalysisData = async () => {
      try {
        setLoading(true);
        setError(null);

        const resultsResponse = await ApiService.getAnalysisResults(taskId);
        
        if (!resultsResponse.analyses || !resultsResponse.analyses[analysisType]) {
          throw new Error(`No data found for analysis type '${analysisType}'.`);
        }

        let analysisData = resultsResponse.analyses[analysisType];
        
        if (typeof analysisData === 'string') {
          if (analysisType === 'elevator_pitch' || analysisType === 'completeness' || analysisType === 'investor_summary') {
            setData(analysisData);
          } else {
            try {
              if (analysisData.startsWith('```json') && analysisData.endsWith('```')) {
                analysisData = analysisData.substring(7, analysisData.length - 3).trim();
              }
              
              try {
                const parsedData = JSON.parse(analysisData);
                setData(parsedData);
              } catch (initialParseError) {
                console.error('Initial parse error:', initialParseError);
                try {
                  const cleanedData = analysisData
                    .replace(/[\u0000-\u001F]+/g, '')
                    .replace(/,\s*]/g, ']')
                    .replace(/,\s*}/g, '}');
                  
                  const parsedData = JSON.parse(cleanedData);
                  setData(parsedData);
                } catch (secondParseError) {
                  console.error('Error parsing even after cleaning:', secondParseError);
                  throw new Error(`Error parsing analysis data: ${initialParseError.message}`);
                }
              }
            } catch (parseError) {
              console.error('Error parsing data:', parseError);
              throw new Error(`Error parsing analysis data: ${parseError.message}`);
            }
          }
        } else {
          setData(analysisData);
        }

      } catch (error) {
        console.error('Error fetching analysis data:', error);
        setError(error.message || 'Error retrieving analysis data');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysisData();
  }, [taskId, analysisType]);

  const getAnalysisTitle = (type) => {
    const titles = {
      'elevator_pitch': 'Elevator Pitch',
      'completeness': 'Completeness Analysis',
      'team': 'Team Analysis',
      'product': 'Product Analysis',
      'market': 'Market Analysis',
      'finance': 'Financial Analysis',
      'strategic_finance': 'Strategic Financial Analysis',
      'investor_summary': 'Investor Summary'
    };
    return titles[type] || type;
  };

  const renderAnalysisDetail = () => {
    if (!data) return null;

    switch (analysisType) {
      case 'elevator_pitch': return <ElevatorPitchDetail data={data} />;
      case 'completeness': return <CompletenessDetail data={data} />;
      case 'team': return <TeamAnalysisDetail data={data} />;
      case 'product': return <ProductAnalysisDetail data={data} />;
      case 'market': return <MarketAnalysisDetail data={data} />;
      case 'finance': return <FinanceAnalysisDetail data={data} />;
      case 'strategic_finance': return <StrategicFinanceAnalysisDetail data={data} />;
      case 'investor_summary': return <InvestorSummaryDetail data={data} />;
      default: return (
        <Box>
          <Typography variant="body1">
            Detail view for this analysis type is not available.
          </Typography>
        </Box>
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ my: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/dashboard/${taskId}`)}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ my: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          {getAnalysisTitle(analysisType)}
        </Typography>
        <AnalysisNavigation currentTab={analysisType} />
      </Box>
      
      <Paper elevation={3} sx={{ p: 3 }}>
        {renderAnalysisDetail()}
      </Paper>
    </Box>
  );
};

export default AnalysisDetailPage;